import React from 'react';
import { useQueryClient } from 'react-query';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { useUpdateUserLocale } from 'client/hooks/user/useUpdateLocale';
import { LocalesInfo, SupportedLocales } from 'client/i18n/types';
import locales from 'client/i18n/locales.json';
import * as events from '../events';
import {
  LanguageButton,
  LanguageGrid,
  Modal,
  ModalBody,
  ModalAsideContent,
  MutedText,
} from './styles';

// Pulling Russian out of Localization launch until further notice
const availableLocales = Object.entries(locales).reduce(
  (acc, [locale, info]) => (locale !== 'ru' ? { ...acc, [locale]: info } : acc),
  {}
);

const pseudoInfo = {
  pseudo: {
    smartling: 'pseudo',
    displayName: 'Pseudo',
    displayNameEn: 'Pseudo',
  },
} as LocalesInfo;

type LanguageSelectionModalProps = {
  onClose: () => void;
};

export function LanguageSelectionModal({
  onClose,
}: LanguageSelectionModalProps): React.ReactElement {
  const {
    state: { userId },
  } = useCurrentUserCtx();
  const t = useT();
  const { locale: currentLocale, changeLocale, showPseudoLang } = useLocale();
  const { updateUserLocale } = useUpdateUserLocale();
  const queryClient = useQueryClient();
  const [
    currentLangRef,
    setCurrentLangRef,
  ] = React.useState<HTMLLIElement | null>(null);

  React.useEffect(() => {
    // Track modal viewed event
    if (userId) {
      events.languageModalSelectorViewed();
    } else {
      events.languageModalSelectorViewedGA();
    }
  }, [userId]);

  React.useEffect(() => {
    if (currentLangRef) currentLangRef.focus();
  }, [currentLangRef]);

  const handleSelectLanguage = (newLocale: SupportedLocales) => {
    if (newLocale !== currentLocale) {
      if (userId) {
        events.languageButtonClicked(newLocale);
        updateUserLocale({
          userId,
          locale: newLocale,
          detectedLocale: window.navigator.language,
        });
      } else {
        events.languageButtonClickedGA(newLocale);
      }

      // Keeps HTML document lang in sync with selected locale for screen readers to use the appropriate pronunciation
      document.documentElement.setAttribute('lang', newLocale);
      document.cookie = `i18n=${newLocale}; SameSite=None; Secure`;
      // Invalidate cache data and force refetch
      queryClient.invalidateQueries();

      changeLocale(newLocale);
    }
    onClose();
  };

  // Allow users select a language using the keyboard
  // by pressing Enter (13) or Space (32) keys.
  const handleKeyPress = (keyCode: number, newLocale: SupportedLocales) => {
    if (keyCode === 13 || keyCode === 32) {
      handleSelectLanguage(newLocale);
    }
  };

  const localesList = {
    ...availableLocales,
    ...(showPseudoLang ? pseudoInfo : {}),
  };

  return (
    <Modal
      aria-label={t(
        'LanguageSelector.LanguageSelectionModal.AriaLabel',
        'Language selection modal',
        'Language selection modal screenreader information'
      )}
      isShowing
      isShowingCloseButton
      onClose={onClose}
    >
      <ModalAsideContent>
        {t(
          'LanguageSelector.LanguageSelectionModal.Title',
          'Choose a language',
          'Language selection modal title'
        )}
      </ModalAsideContent>
      <ModalBody as="div">
        <LanguageGrid>
          {Object.entries(localesList as LocalesInfo).map(
            ([locale, localeInfo]) => {
              return (
                <li key={locale}>
                  <LanguageButton
                    ref={
                      locale === currentLocale
                        ? (node: HTMLLIElement) => setCurrentLangRef(node)
                        : null
                    }
                    onClick={() =>
                      handleSelectLanguage(locale as SupportedLocales)
                    }
                    onKeyPress={(event: React.KeyboardEvent) =>
                      handleKeyPress(event.charCode, locale as SupportedLocales)
                    }
                  >
                    <span>{localeInfo.displayName}</span>
                    <MutedText>{localeInfo.displayNameEn}</MutedText>
                  </LanguageButton>
                </li>
              );
            }
          )}
        </LanguageGrid>
      </ModalBody>
    </Modal>
  );
}
